/**
 * The right column with no session open — the A1 canon's day: a heading and a count, the day's
 * sessions and blocks in time order with a "now" rule, then what waits for the trainer's decision
 * and the week's free starts at the foot.
 *
 * A session row is a button that opens the same panel the grid's block opens, so the list is a
 * second, keyboard-first way into every session on the day.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { capitaliseFirst } from '~/shared/lib/localeCase';
import { EmptyState } from '~/shared/ui/EmptyState';
import { CalendarMarkIcon } from '~/shared/ui/EmptyStateIcons';
import chip from '~/shared/ui/neutralChip.module.css';
import skeleton from '~/shared/ui/skeleton.module.css';
import { ChevronGlyph } from '~/shared/ui/VideoGlyphs';
import { ChevronRightIcon } from '~/shared/ui/AuthIcons';

import type { BookingCalendarState } from '../lib/useBookingCalendar';
import { dayMonth, longWeekday, weekdayClock, weekdayDateClock } from '../model/calendarWords';
import { callLinkView } from '../model/callLink';
import type { AgendaRow, DayAgenda, PillTone } from '../model/dayAgenda';
import { kindWordKey, placeKeyOf } from '../model/sessionDetail';
import styles from './SidePanel.module.css';

const DOT_CLASS: Readonly<Record<PillTone, string | undefined>> = {
  good: styles.dotGood,
  accent: styles.dotAccent,
  warn: styles.dotWarn,
  danger: styles.dotDanger,
  neutral: styles.dotNeutral,
};

interface DayAgendaPanelProps {
  readonly state: BookingCalendarState;
  readonly agenda: DayAgenda;
  /** Nothing is drawn anywhere in the shown range — the empty sentence names the week or month. */
  readonly rangeIsEmpty: boolean;
  /** Opens a session in the column — the caller remembers where focus came from. */
  readonly onOpenSession: (id: number) => void;
  /** A day back or forward — the caller keeps focus on the control across a page of the grid. */
  readonly onStepDay: (direction: -1 | 1) => void;
  /** Which step control held focus before the column was redrawn, to hand it back on mount. */
  readonly refocusStep: -1 | 1 | null;
  /** Called once focus has been handed back, so a later redraw does not take it again. */
  readonly onRefocused: () => void;
}

export function DayAgendaPanel({
  state,
  agenda,
  rangeIsEmpty,
  onOpenSession,
  onStepDay,
  refocusStep,
  onRefocused,
}: DayAgendaPanelProps) {
  const { t, i18n } = useTranslation();
  const previousDay = useRef<HTMLButtonElement>(null);
  const nextDay = useRef<HTMLButtonElement>(null);

  // Stepping past the range pages the grid, and the column is redrawn after the new week's read:
  // the control the trainer was pressing gets focus back.
  useEffect(() => {
    if (refocusStep === null) return;
    (refocusStep === -1 ? previousDay : nextDay).current?.focus();
    onRefocused();
  }, [refocusStep, onRefocused]);
  const locale = i18n.language;
  const weekday = longWeekday(state.selectedDay, locale);
  const date = dayMonth(state.selectedDay);
  const heading =
    state.selectedDay === state.today
      ? t('web:booking.agenda.today', { weekday, date })
      : t('web:booking.agenda.day', { weekday: capitaliseFirst(weekday, locale), date });

  const renderRow = (row: AgendaRow) => {
    switch (row.kind) {
      case 'now':
        return (
          <li key="now" className={styles.nowRow}>
            <span className={styles.nowClock}>{row.clock}</span>
            <span className={styles.nowRule} aria-hidden="true" />
          </li>
        );
      case 'block':
        return (
          <li key={`block-${String(row.block.id)}`} className={`${String(styles.row)} ${String(styles.blockRow)}`}>
            <span className={styles.rowTime}>
              <span className={styles.rowStart}>{row.startClock}</span>
              <span className={styles.rowEnd}>{row.endClock}</span>
            </span>
            <span className={styles.rowBody}>
              <span className={styles.rowName}>{t('web:booking.agenda.block')}</span>
              <span className={styles.rowSub}>
                {[row.block.note, t('web:booking.agenda.blockPrivate')].filter(Boolean).join(' · ')}
              </span>
            </span>
          </li>
        );
      case 'session': {
        const { session, pill } = row;
        const name = session.clientName ?? t('web:booking.unnamedClient');
        const kind = t(kindWordKey(session));
        const place = t(placeKeyOf(session));
        // Where the call is held, at a glance; the link itself is opened from the session panel.
        const callHost = session.isVideoCall ? callLinkView(session.callLink)?.host : undefined;
        const host = callHost === undefined ? '' : ` · ${callHost}`;
        return (
          <li key={session.id}>
            <button
              type="button"
              className={`${String(styles.row)} ${String(styles.sessionRow)} ${row.isNext ? String(styles.nextRow) : ''}`}
              onClick={() => {
                onOpenSession(session.id);
              }}
            >
              <span className={styles.rowTime}>
                <span className={styles.rowStart}>{session.startClock ?? '—'}</span>
                <span className={styles.rowEnd}>{session.endClock}</span>
              </span>
              <span className={styles.rowBody}>
                <span className={styles.rowName}>{name}</span>
                <span className={styles.rowSub}>
                  {session.author === 'client' ? (
                    <>
                      <span className={styles.originTag}>{t('web:booking.agenda.clientBooked')}</span>
                      {` · ${kind.toLocaleLowerCase(locale)}${host}`}
                    </>
                  ) : (
                    `${kind} · ${place}${host}`
                  )}
                </span>
              </span>
              {pill === null ? null : (
                <span className={`${String(chip.neutralChip)} ${String(styles.pill)}`}>
                  <i className={`${String(styles.dot)} ${String(DOT_CLASS[pill.tone])}`} aria-hidden="true" />
                  <span className={pill.strike ? styles.struck : undefined}>
                    {t(pill.labelKey, pill.params)}
                  </span>
                </span>
              )}
            </button>
          </li>
        );
      }
    }
  };

  const listed = agenda.rows.some((row) => row.kind !== 'now');
  const noSessions = !agenda.rows.some((row) => row.kind === 'session');

  return (
    <div className={styles.agenda}>
      <div className={styles.head}>
        <div className={styles.headRow}>
          <h2 className={styles.title}>{heading}</h2>
          {/* The keyboard's way to ANY day, a free one included: the grid's arrows walk sessions
            * only. Leaving the shown range pages the grid. */}
          <span className={styles.dayStep}>
            <button
              ref={previousDay}
              type="button"
              className={styles.dayStepButton}
              aria-label={t('web:booking.agenda.previousDay')}
              onClick={() => {
                onStepDay(-1);
              }}
            >
              <ChevronGlyph className={styles.dayStepGlyph} direction="left" />
            </button>
            <button
              ref={nextDay}
              type="button"
              className={styles.dayStepButton}
              aria-label={t('web:booking.agenda.nextDay')}
              onClick={() => {
                onStepDay(1);
              }}
            >
              <ChevronGlyph className={styles.dayStepGlyph} direction="right" />
            </button>
          </span>
        </div>
        <p className={styles.note}>
          {`${t('web:booking.agenda.count', { count: agenda.sessionCount })} · ${t('web:booking.agenda.hint')}`}
        </p>
      </div>

      {listed ? <ul className={styles.rows}>{agenda.rows.map(renderRow)}</ul> : null}
      {noSessions ? (
        <EmptyState
          className={styles.empty}
          mark={<CalendarMarkIcon />}
          tone="neutral"
          title={t(
            !rangeIsEmpty
              ? 'web:booking.agenda.emptyDay'
              : state.view === 'month'
                ? 'web:booking.agenda.emptyMonth'
                : 'web:booking.agenda.emptyWeek',
          )}
        />
      ) : null}

      {state.awaiting.length === 0 ? null : (
        <section className={styles.section} aria-labelledby="booking-decisions">
          <h3 id="booking-decisions" className={styles.eyebrow}>
            {t('web:booking.agenda.decisions')}
          </h3>
          <ul className={styles.decisions}>
            {state.awaiting.map((session) => (
              <li key={session.id}>
                <button
                  type="button"
                  className={styles.decision}
                  onClick={() => {
                    onOpenSession(session.id);
                  }}
                >
                  <i className={`${String(styles.dot)} ${String(styles.dotDanger)}`} aria-hidden="true" />
                  <span className={styles.rowBody}>
                    <span className={styles.rowName}>
                      {session.clientName ?? t('web:booking.unnamedClient')}
                    </span>
                    <span className={styles.rowSub}>
                      {[
                        session.slotMs === null
                          ? null
                          : weekdayDateClock(session.slotMs, state.zone, locale),
                        t('web:booking.agenda.awaiting'),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <ChevronRightIcon className={styles.decisionGlyph} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {state.freeStarts.kind === 'hidden' ? null : (
        <section className={`${String(styles.section)} ${String(styles.foot)}`} aria-labelledby="booking-free-starts">
          <h3 id="booking-free-starts" className={styles.eyebrow}>
            {t('web:booking.agenda.freeStarts')}
          </h3>
          {state.freeStarts.kind === 'failed' ? (
            <p className={`${String(styles.note)} ${String(styles.quietFailure)}`}>
              {t('web:booking.agenda.freeStartsFailed')}
              <button type="button" className={styles.inlineRetry} onClick={state.retryFreeStarts}>
                {t('calendar.retry')}
              </button>
            </p>
          ) : state.freeStarts.kind === 'pending' ? (
            // Re-reading another week's starts: the foot's own wait, the size of one chip row.
            <span className={`${String(skeleton.bar)} ${String(styles.startsPending)}`} aria-hidden="true" />
          ) : state.freeStarts.starts.length === 0 ? (
            <p className={styles.note}>{t('web:booking.agenda.freeStartsNone')}</p>
          ) : (
            <ul className={styles.starts}>
              {state.freeStarts.starts.map((start) => (
                <li key={start.day} className={styles.start}>
                  {weekdayClock(start.startMs, state.zone, locale)}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
